import { useEffect, useState } from "react";
import {
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    Archive,
    FolderPlus,
    X,
    Sparkles
} from "lucide-react";
import toast from "react-hot-toast";

import {
    getUnclaimedPreviewSessions,
    markPreviewSessionExpired,
    claimPreviewSessionAsAdmin
} from "../../services/crmService";

import "./UnclaimedPreviewSessionsPanel.css";

export default function UnclaimedPreviewSessionsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState("");
    const [confirmAction, setConfirmAction] = useState(null);

    const load = async () => {
        setLoading(true);

        try {
            const result = await getUnclaimedPreviewSessions(20);
            setItems(result?.items || []);
        } catch (error) {
            console.error("Failed to load unclaimed preview sessions", error);
            toast.error("Could not load unclaimed preview sessions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openClaimConfirm = (item) => {
        setConfirmAction({
            type: "claim",
            item,
            title: "Convert preview into project?",
            message: `"${item.businessName || "Untitled preview"}" will become a real project under SuperAdmin. You can assign it to a client account from the project page later.`,
            confirmText: "Mark claimed",
            icon: FolderPlus
        });
    };

    const openExpireConfirm = (item) => {
        setConfirmAction({
            type: "expire",
            item,
            title: "Mark preview as expired?",
            message: `"${item.businessName || "Untitled preview"}" will be removed from the recovery list and kept in audit logs.`,
            confirmText: "Mark expired",
            icon: Archive
        });
    };

    const closeConfirm = () => {
        if (busyId) return;
        setConfirmAction(null);
    };

    const runConfirmedAction = async () => {
        if (!confirmAction?.item) return;

        const item = confirmAction.item;

        try {
            setBusyId(item.id);

            if (confirmAction.type === "expire") {
                await markPreviewSessionExpired(
                    item.id,
                    "Marked expired manually from SuperAdmin dashboard."
                );

                setConfirmAction(null);
                toast.success("Preview session marked as expired.");
                await load();
                return;
            }

            if (confirmAction.type === "claim") {
                const result = await claimPreviewSessionAsAdmin(item.id);

                const projectId =
                    result?.projectId ||
                    result?.claimedProjectId ||
                    result?.project?.id ||
                    "";

                setConfirmAction(null);

                if (projectId) {
                    toast.success("Preview converted into a real project.");
                    window.location.href = `/crm/projects/${projectId}`;
                    return;
                }

                await load();
                toast.warning("Preview was claimed, but project id was not returned. Please check Projects list.");
            }
        } catch (error) {
            console.error("Preview session action failed", error);

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Action failed. Please check System Logs.";

            toast.error(message);
        } finally {
            setBusyId("");
        }
    };

    return (
        <section className="unclaimed-preview-card">
            <div className="unclaimed-preview-head">
                <div>
                    <span>SuperAdmin recovery</span>
                    <strong>Unclaimed preview sessions</strong>
                </div>

                <button type="button" onClick={load} disabled={loading}>
                    <RefreshCw size={15} />
                    {loading ? "Loading..." : "Refresh"}
                </button>
            </div>

            {loading ? (
                <p className="unclaimed-preview-empty">Loading preview sessions...</p>
            ) : null}

            {!loading && items.length === 0 ? (
                <p className="unclaimed-preview-empty">
                    No unclaimed preview sessions found.
                </p>
            ) : null}

            <div className="unclaimed-preview-list">
                {items.map((item) => {
                    const busy = busyId === item.id;

                    return (
                        <article key={item.id} className="unclaimed-preview-item">
                            <div className="unclaimed-preview-main">
                                <strong>{item.businessName || "Untitled preview"}</strong>

                                <span>
                                    {item.industry || "Unknown industry"}
                                    {item.subIndustry ? ` / ${item.subIndustry}` : ""}
                                </span>

                                <small>{formatDate(item.createdAtUtc)}</small>
                            </div>

                            <div className="unclaimed-preview-badges">
                                <StatusBadge ok={item.hasPreviewHtml} label="Preview" />
                                <StatusBadge ok={item.hasAiResponse} label="AI" />
                                <StatusBadge ok={item.hasGeneratedPages} label="Pages" />
                                {item.status ? <em>{item.status}</em> : null}
                            </div>

                            {item.errorMessage ? (
                                <p className="unclaimed-preview-error">
                                    <AlertTriangle size={14} />
                                    {item.errorMessage}
                                </p>
                            ) : null}

                            <div className="unclaimed-preview-actions">
                                <button
                                    type="button"
                                    onClick={() => openClaimConfirm(item)}
                                    disabled={busy}
                                    className="claim"
                                >
                                    <FolderPlus size={14} />
                                    {busy ? "Working..." : "Mark claimed"}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => openExpireConfirm(item)}
                                    disabled={busy}
                                >
                                    <Archive size={14} />
                                    {busy ? "Working..." : "Mark expired"}
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>

            {confirmAction ? (
                <PremiumConfirmModal
                    action={confirmAction}
                    busy={!!busyId}
                    onCancel={closeConfirm}
                    onConfirm={runConfirmedAction}
                />
            ) : null}
        </section>
    );
}

function PremiumConfirmModal({ action, busy, onCancel, onConfirm }) {
    const Icon = action.icon || Sparkles;

    return (
        <div className="snx-confirm-backdrop" role="presentation" onClick={onCancel}>
            <section
                className="snx-confirm-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="snx-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="snx-confirm-glow" />

                <button
                    type="button"
                    className="snx-confirm-close"
                    onClick={onCancel}
                    disabled={busy}
                    aria-label="Close"
                >
                    <X size={18} />
                </button>

                <div className="snx-confirm-icon">
                    <Icon size={22} />
                </div>

                <span className="snx-confirm-kicker">SuperAdmin action</span>

                <h3 id="snx-confirm-title">{action.title}</h3>

                <p>{action.message}</p>

                <div className="snx-confirm-actions">
                    <button
                        type="button"
                        className="snx-confirm-secondary"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="snx-confirm-primary"
                        onClick={onConfirm}
                        disabled={busy}
                    >
                        {busy ? "Working..." : action.confirmText}
                    </button>
                </div>
            </section>
        </div>
    );
}

function StatusBadge({ ok, label }) {
    return (
        <span className={ok ? "ok" : "missing"}>
            <CheckCircle2 size={13} />
            {label}
        </span>
    );
}

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}